import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";

import {
  updateTournament,
  selectCategory,
} from "../../redux/slices/tournamentSlice";
import ClubbieButton from "../ClubbieButton/ClubbieButton";
import ClubbieModal from "../Modal/ClubbieModal";
import DatePicker from "../DatePicker/DatePicker";

/**
 * UpdateTournamentSchedule - JSX component with Tailwind CSS
 * Modal for updating tournament match schedule (start/end dates)
 * 
 * Features:
 * - Dual date pickers for start and end dates
 * - Date range validation (start before end)
 * - Auto-selects default division/season if needed
 * - Toast notifications
 * 
 * @param {boolean} isOpen - Modal visibility state
 * @param {function} onClose - Callback to close modal
 */
const UpdateTournamentSchedule = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const selectedTournament = useSelector(
    (state) => state.tournament.selectedTournament
  );

  const [isLoading, setIsLoading] = useState(false);

  const [schedule, setSchedule] = useState({
    startDate: selectedTournament?.matchSchedule?.startDate || "",
    endDate: selectedTournament?.matchSchedule?.endDate || "",
  });

  const handleDateChange = (field, value) => {
    setSchedule((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateNoOfTeams = async () => {
    try {
      setIsLoading(true);
      const updateData = {
        matchSchedule: schedule,
      };
      const result = await dispatch(
        updateTournament({
          id: selectedTournament?.id,
          updateData,
        })
      ).unwrap();

      if (result) {
        if (!selectedTournament?.categories?.length && result.categories?.length) {
          dispatch(selectCategory(result.categories[0]));
        }
        toast.success("Competition schedule updated successfully!");
        onClose();
      }
    } catch (error) {
      toast.error(error.message || "Failed to update competition schedule");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ClubbieModal
      isOpen={isOpen}
      toggle={onClose}
      title="Update Competition Schedule"
    >
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-2">
          <label className="text-[#FEFCEB] text-sm font-medium">
            Start Date <span className="text-red-500 ml-1">*</span>
          </label>
          <DatePicker
            value={schedule.startDate}
            onChange={(value) => handleDateChange("startDate", value)}
            className="w-full"
            type="date"
            maxDate={schedule.endDate}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-[#FEFCEB] text-sm font-medium">
            End Date <span className="text-red-500 ml-1">*</span>
          </label>
          <DatePicker
            value={schedule.endDate}
            onChange={(value) => handleDateChange("endDate", value)}
            className="w-full"
            type="date"
            minDate={schedule.startDate}
          />
        </div>

        <ClubbieButton
          className="w-full"
          onClick={handleUpdateNoOfTeams}
          disabled={isLoading || (!schedule.endDate && !schedule.startDate)}
          isLoading={isLoading}
        >
          Update
        </ClubbieButton>
      </div>
    </ClubbieModal>
  );
};

export default UpdateTournamentSchedule;
